import { getLessonTypeMeta } from './lessonTypeLabels';

// Goal colors are not declared here — each goal borrows the hue of the
// lesson type it measures, so chips, rings and charts can't drift apart.
const typeHex = (lessonType) => getLessonTypeMeta(lessonType).hex;

/**
 * Canonical list of daily goal types.
 * Single source of truth for Settings, DailyGoalRing, calcStreak, DailyStatsTab.
 */
export const GOAL_TYPES = [
  {
    key: 'words',
    label: 'Wörter',
    statsField: 'words_answered_today',
    defaultTarget: 30,
    step: 5,
    color: typeHex('production'),
  },
  {
    key: 'genus',
    label: 'Genus',
    statsField: 'genus_reviews',
    defaultTarget: 20,
    step: 5,
    color: typeHex('article_gender'),
  },
  {
    key: 'objects',
    label: 'Objekte',
    statsField: 'object_gap_questions',
    defaultTarget: 5,
    step: 1,
    color: typeHex('preposition_gap'),
  },
  {
    key: 'verb_forms',
    label: 'Konjugation',
    statsField: 'verb_forms_questions',
    defaultTarget: 5,
    step: 1,
    color: typeHex('verb_forms'),
  },
  {
    key: 'nvv',
    label: 'Nomen-Verb',
    statsField: 'nvv_questions',
    defaultTarget: 5,
    step: 1,
    color: typeHex('verb_collocation'),
  },
  {
    key: 'reflexive',
    label: 'Reflexiv',
    statsField: 'reflexive_questions',
    defaultTarget: 5,
    step: 1,
    color: typeHex('reflexive'),
  },
  {
    key: 'plural',
    label: 'Plural',
    statsField: 'plural_questions',
    defaultTarget: 5,
    step: 1,
    color: typeHex('plural'),
  },
  {
    key: 'recognition',
    label: 'Erkennen',
    statsField: 'recognition_questions',
    defaultTarget: 20,
    step: 5,
    color: typeHex('recognition'),
  },
];

/** Get the actual value for a goal from a day stats object */
export const getGoalActual = (dayStats, goalKey) => {
  const goalType = GOAL_TYPES.find((g) => g.key === goalKey);
  if (!goalType || !dayStats) return 0;
  return Number(dayStats[goalType.statsField] || 0);
};

/** Get active goals as array of { key, target, ...goalTypeMeta } */
export const getActiveGoals = (dailyGoals) => {
  if (!dailyGoals) return [];
  return GOAL_TYPES.filter((g) => dailyGoals[g.key] != null && dailyGoals[g.key] > 0).map((g) => ({
    ...g,
    target: dailyGoals[g.key],
  }));
};

/**
 * Goals to judge a past day by: the day's own `goal_snapshot` (the goals
 * active on that date, reconstructed by the backend from goal-change
 * history) when present, otherwise the current goals. Keeps the streak,
 * the 7-day list and the calendar from retroactively failing old days
 * after a goal is added or raised.
 */
export const getEffectiveGoals = (dayStats, currentGoals) => {
  if (dayStats?.goal_snapshot && Object.keys(dayStats.goal_snapshot).length > 0) {
    return dayStats.goal_snapshot;
  }
  return currentGoals;
};

/** Check if all active goals are met for a day stats object */
export const isGoalMet = (dayStats, dailyGoals) => {
  const active = getActiveGoals(dailyGoals);
  if (active.length === 0) return true; // no goals = any activity counts
  return active.every((g) => getGoalActual(dayStats, g.key) >= g.target);
};

/** Check if a day has any activity (questions_answered includes all drill types) */
export const isDayActive = (dayStats) => {
  if (!dayStats) return false;
  return (dayStats.questions_answered || 0) > 0;
};
